<script lang="ts">
    import { useLocaleContext, useShopContext } from '$lib/context';
    import FreeDeliveryGauge from '$lib/components/cart/FreeDeliveryGauge.svelte';
    import type { ApiSchemas } from '$lib/api/shopwareApi';
    import { createQuery } from '@tanstack/svelte-query';
    import { CartApi } from '$lib/api/cartApi';
    import CartLineItem from '$lib/components/cart/CartLineItem.svelte';
    import { formatCurrency } from '$lib/util/currencyUtil';
    import { t, number } from 'svelte-i18n';
    import ButtonVV from '$lib/daisyUiComponents/ButtonVV.svelte';
    import { onMount } from 'svelte';

    export let cartSideBarOpen: boolean = false;

    const lang = useLocaleContext();
    const shopContext = useShopContext();

    $: cartQuery = createQuery({
        queryKey: ['cart'],
        queryFn: () => CartApi.getCart(shopContext),
        staleTime: 30 * 1000,
        gcTime: 120 * 1000
    });

    let cart: ApiSchemas['Cart'];
    $: cart = $cartQuery.data;

    $: cartLineItems = (cart?.lineItems?.filter((li) => li.type === 'product') as ApiSchemas['LineItem'][]) ?? [];
    $: cartPromotions = cart?.lineItems?.filter((li) => li.type === 'promotion') ?? [];

    function getSumOfPrices(lineItems: ApiSchemas['LineItem'][]) {
        let sum = 0;
        lineItems.forEach((lineItem: ApiSchemas['LineItem']) => {
            sum += lineItem.price.totalPrice;
        });
        return sum;
    }

    function closeSideBar() {
        cartSideBarOpen = false;
    }

    onMount(() => {
        window.addEventListener('click', closeSideBar);

        return () => {
            window.removeEventListener('click', closeSideBar);
        };
    });
</script>

{#if cart}
    <div
        class="{cartSideBarOpen
            ? 'pointer-events-auto opacity-100'
            : 'pointer-events-none opacity-0'} transition-opacity duration-300 font-jost z-40 fixed top-0 left-0 flex justify-end overflow-y-scroll h-screen w-full"
    >
        <button class="fixed top-0 left-0 w-full h-full bg-black/50" />
        <div
            on:click|stopPropagation
            tabindex="-1"
            role="presentation"
            class="{cartSideBarOpen
                ? 'translate-x-0'
                : 'translate-x-8'} min-h-[100vh] transition-transform duration-300 z-40 drop-shadow-lg lg:mt-16 mt-[7.5rem] p-5 right-0 bg-base-100 max-w-md h-fit w-full flex flex-col gap-7"
        >
            <button
                on:click={() => (cartSideBarOpen = false)}
                class="text-secondary text-sm uppercase font-medium flex gap-3 items-center"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="12" viewBox="0 0 15 12" fill="none">
                    <path d="M14.75 6H2.75" stroke="#477785" stroke-width="1.5" />
                    <path d="M7 1L2 6.00001L7 11" stroke="#477785" stroke-width="1.5" />
                </svg>
                {$t('shop.cart.keep-shopping')}
            </button>
            <FreeDeliveryGauge {cart} />
            <div class="flex flex-col gap-1">
                <small class="uppercase text-primary tracking-widest font-medium">{$t('shop.cart.small')}</small>
                <h1 class="font-cinzel uppercase text-black text-4xl">{$t('shop.cart.big')}</h1>
            </div>
            <div class="flex flex-col gap-3">
                {#each cartLineItems as lineItem}
                    <CartLineItem {lineItem} />
                {:else}
                    <div class="bg-white px-8 py-6 text-neutral-500">{$t('shop.cart.no-items')}</div>
                {/each}
                <div class="px-8 py-6 bg-white text-neutral-500 text-sm gap-3 flex flex-col">
                    <div class="flex justify-between items-end">
                        <p class="text-secondary font-medium">
                            {shopContext.storeContext?.customer
                                ? $t('shop.cart.menu.total')
                                : $t('shop.cart.menu.sub-total')}
                        </p>
                        {#if cart.price?.totalPrice || cart.price?.netPrice}
                            <div class="text-xl lg:text-2xl text-secondary">
                                {#if cart.price.taxStatus === 'net'}
                                    {formatCurrency($number, shopContext.storeContext, cart.price.netPrice)}
                                {:else}
                                    {formatCurrency($number, shopContext.storeContext, cart.price.totalPrice)}
                                {/if}
                            </div>
                        {:else}
                            <div class="text-xl lg:text-2xl text-secondary">
                                {formatCurrency($number, shopContext.storeContext, 0)}
                            </div>
                        {/if}
                    </div>
                    <div class="h-[1px] bg-neutral-500" />
                    <div class="flex justify-between">
                        <p>
                            {$t('shop.cart.menu.promotions')}
                        </p>
                        <p>{formatCurrency($number, shopContext, getSumOfPrices(cartPromotions))}</p>
                    </div>
                    <div class="flex justify-between">
                        <p>
                            {$t('shop.cart.menu.tax')} ({cart.price.calculatedTaxes.length
                                ? cart.price.calculatedTaxes[0].taxRate
                                : 0}%)
                        </p>
                        <p>{formatCurrency($number, shopContext, cart.price.totalPrice - cart.price.netPrice)}</p>
                    </div>
                    <div class="flex justify-between">
                        <p>
                            {cartLineItems.length}
                            {$t('shop.cart.menu.products')}
                        </p>
                        <p>{formatCurrency($number, shopContext, getSumOfPrices(cartLineItems))}</p>
                    </div>
                </div>
            </div>
            <div class="flex flex-col pb-20 gap-3">
                <ButtonVV
                    disabled={cartLineItems.length < 1}
                    on:click={() => (cartSideBarOpen = false)}
                    href="/{lang}/checkout"
                    color="primary">{$t('shop.cart.to-checkout')}</ButtonVV
                >
                <ButtonVV
                    on:click={() => (cartSideBarOpen = false)}
                    href="/{lang}/cart"
                    color="primary"
                    btnStyle="outline">{$t('shop.cart.show-cart')}</ButtonVV
                >
            </div>
        </div>
    </div>
{/if}
